

import { Link, useLocation } from "react-router-dom"
import { useContext, useEffect, useState, useRef } from "react"
import { AuthContext } from "./AuthContext"
import Button from "./Button"
import clsx from "clsx"
import LatexGenerator from "./topicSelectorComponents/LectureNotesGenerator"
import SlidesGenerator from "./topicSelectorComponents/SlidesGenerator"

const CancelButton = ({ children, ...rest }) => {
  const additionalClassName =
    "!text-black whitespace-nowrap px-6 py-2 border border-gray-300 bg-white hover:bg-gray-50 "

  const combinedClassName = clsx(additionalClassName, rest.className)
  return (
    <Button className={combinedClassName} {...rest}>
      {children}
    </Button>
  )
}

export default function Navigator() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const [showLecturePopup, setShowLecturePopup] = useState(false)
  const [showSlidesPopup, setSlidesPopup] = useState(false)
  const [hoveredItem, setHoveredItem] = useState(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [showShineEffect, setShowShineEffect] = useState(true)
  const [recentlyUsedIds, setRecentlyUsedIds] = useState([
    "course-helper",
    "plot-generator",
    "notes-generation",
    "slides-generation",
  ])
  const [likedIds, setLikedIds] = useState([
    "course-helper",
    "plot-generator",
    "notes-generation",
    "math-papers",
    "compile-latex",
  ])

  const location = useLocation()
  const currentPath = location.pathname
  const isHomePage = currentPath === "/home"

  const mobileMenuRef = useRef(null)
  const sidebarRef = useRef(null)

  // Add shine effect that stops after user clicks sidebar button
  useEffect(() => {
    if (showSidebar) {
      setShowShineEffect(false)
    }
  }, [showSidebar])

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar)
    setShowShineEffect(false)
  }

  const { user, logout, isGuest } = useContext(AuthContext)

  const openLogoutModal = () => {
    setIsLogoutModalOpen(true)
  }

  const closeLogoutModal = () => {
    setIsLogoutModalOpen(false)
  }

  const handleLogout = async () => {
    try {
      await logout()
      console.log("Logged out successfully")
      closeLogoutModal()
    } catch (error) {
      console.error("Logout error: ", error)
    }
  }

  useEffect(() => {
    function handleClickOutside(event) {
      // Only handle mobile menu if it's open
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest('button[aria-expanded="true"]')
      ) {
        setTimeout(() => {
          setIsMobileMenuOpen(false)
        }, 100)
      }

      // Only close sidebar if clicking outside both the button and the sidebar area
      if (
        showSidebar &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest(".sidebar-trigger")
      ) {
        setShowSidebar(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showSidebar, isMobileMenuOpen])

  const handleItemClick = (itemId) => {
    // Update recently used
    setRecentlyUsedIds((prev) => {
      const filtered = prev.filter((id) => id !== itemId)
      return [itemId, ...filtered].slice(0, 5) // Keep only top 5
    })
  }

  const toggleLiked = (itemId, event) => {
    event.stopPropagation()
    event.preventDefault()
    setLikedIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId)
      } else {
        return [...prev, itemId]
      }
    })
  }

  // Replace the existing sidebarItems array with this updated version
  const sidebarItems = [
    {
      id: "course-helper",
      name: "Course Helper",
      icon: "fas fa-graduation-cap",
      color: "text-emerald-500",
      link: "/topic-selector",
      description: "Get help with your courses and topics.",
    },
    {
      id: "plot-generator",
      name: "Plot Generator",
      icon: "fas fa-chart-line",
      color: "text-blue-500",
      link: "/plot-generator",
      description: "Generate mathematical plots and graphs.",
    },
    {
      id: "notes-generation",
      name: "Notes Generation",
      icon: "fas fa-edit",
      color: "text-emerald-500",
      onClick: () => setShowLecturePopup(true),
      description: "Generate detailed notes from lectures.",
    },
    {
      id: "slides-generation",
      name: "Slides Generation",
      icon: "fas fa-file-powerpoint",
      color: "text-rose-500",
      onClick: () => setSlidesPopup(true),
      description: "Create presentation slides using AI.",
    },
    {
      id: "math-papers",
      name: "Math Papers",
      icon: "fas fa-folder-open",
      color: "text-blue-500",
      link: "/google-drive",
      description: "Access previous papers for study and practice.",
    },
    {
      id: "calculator",
      name: "Calculator",
      icon: "fas fa-calculator",
      color: "text-blue-500",
      link: "/calculator",
      description: "Use the calculator for mathematical operations.",
    },
    {
      id: "image-to-text",
      name: "Image to Text",
      icon: "fas fa-file-image",
      color: "text-purple-500",
      link: "/image-text-convertor",
      description: "Convert images with text into editable content.",
    },
    {
      id: "compile-latex",
      name: "Compile Latex",
      icon: "fas fa-superscript",
      color: "text-teal-500",
      link: "/latex-editor",
      description: "Write and compile LaTeX documents easily.",
    },
    {
      id: "knowledge-galaxy",
      name: "Knowledge Galaxy",
      icon: "fas fa-asterisk",
      color: "text-indigo-500",
      link: "/knowledge-network",
      description: "Explore knowledge in an interactive galaxy view.",
    },
    {
      id: "formula-search",
      name: "Formula Search",
      icon: "fas fa-square-root-alt",
      color: "text-amber-500",
      link: "/formula-search",
      description: "Find mathematical formulas quickly.",
    },
        {
      id: "question-search",
      name: "Question Search",
      icon: "fas fa-square-root-alt",
      color: "text-amber-500",
      link: "/question-search",
      description: "Find real test question from database.",
    },
    {
      id: "pdf-editor",
      name: "PDF Editor",
      icon: "fas fa-file-pdf",
      color: "text-orange-500",
      link: "/pdf-editor",
      description: "Edit and manage your PDFs with ease.",
    },
  ]

  // Group sidebar items by recently used and liked
  const recentlyUsedItems = recentlyUsedIds.map((id) => sidebarItems.find((item) => item.id === id)).filter(Boolean)
  const likedItems = sidebarItems.filter((item) => likedIds.includes(item.id))
  const otherItems = sidebarItems.filter(
    (item) => !recentlyUsedIds.slice(0, 3).includes(item.id) && !likedIds.includes(item.id),
  )

  // Updated nav links (removed Course Helper and Plot Generator)
  const navLinks = [
    { name: "Chatbox", path: "/chatbox", icon: "fas fa-comments" },
    { name: "Past Papers", path: "/past-papers", icon: "fas fa-folder-open" },
    { name: "Exam Grader", path: "/exam-grader", icon: "fas fa-check-square" },
    { name: "Quiz Generator", path: "/quiz-generator", icon: "fas fa-search" },
    { name: "Home Page", path: "/home", icon: "fas fa-home", highlight: true },
  ]

  return (
    <>
      <style jsx="true">{`
        @keyframes shine {
          0% {
            background-position: -100% 0;
          }
          50% {
            background-position: 100% 0;
          }
          100% {
            background-position: -100% 0;
          }
        }
        .shine-effect {
          position: relative;
          overflow: hidden;
        }
        .shine-effect::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.3) 20%,
            rgba(255, 255, 255, 0) 50%
          );
          background-size: 200% 100%;
          animation: shine 3s infinite linear;
          z-index: 1;
          pointer-events: none;
        }
        .sidebar-enter {
          transform: translateX(-100%);
          opacity: 0;
        }
        .sidebar-enter-active {
          transform: translateX(0);
          opacity: 1;
          transition: transform 0.3s ease-out, opacity 0.3s ease-out;
        }
        .sidebar-exit {
          transform: translateX(0);
          opacity: 1;
        }
        .sidebar-exit-active {
          transform: translateX(-100%);
          opacity: 0;
          transition: transform 0.3s ease-in, opacity 0.3s ease-in;
        }
      `}</style>
      <div className="App max-h-screen flex flex-col">
        <nav
          className={`${isHomePage ? "bg-blue-900" : "bg-black bg-opacity-100"} backdrop-blur-md text-white shadow-md z-50 sticky top-0 transition-colors duration-500`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex-shrink-0 flex items-center group">
                  <span className="text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-purple-500 group-hover:from-purple-500 group-hover:to-cyan-400 transition-all duration-500">
                    AIAT
                  </span>
                </Link>
                <div className="hidden md:ml-10 md:flex md:space-x-1">
                  {navLinks.map((link) => {
                    const isActive = currentPath === link.path
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`relative px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ease-in-out flex items-center space-x-1.5 group whitespace-nowrap ${
                          isActive ? "text-white" : "text-gray-300 hover:text-white"
                        }`}
                      >
                        <i
                          className={`${link.icon} transition-all duration-300 ${isActive ? "text-cyan-400 scale-110" : "group-hover:text-cyan-400 group-hover:scale-110"}`}
                        ></i>
                        <span>{link.name}</span>
                        {isActive && (
                          <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"></span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>

              {/* Right side - Mini apps and profile */}
              <div className="flex items-center space-x-4 ml-8">
                {/* Mini apps toggle button - Original "More Functions" button */}
                <div className="relative">
                  <button
                    onClick={toggleSidebar}
                    className="sidebar-trigger p-2 rounded-lg hover:bg-gray-800 hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center group"
                    aria-label="Toggle sidebar"
                    aria-expanded={showSidebar}
                  >
                    <div className="flex items-center">
                      <div
                        className={`grid grid-cols-3 gap-1 w-6 h-6 transition-all duration-300 ${showSidebar ? "rotate-45 scale-90" : "rotate-0"}`}
                      >
                        {[...Array(9)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${showSidebar ? "bg-cyan-400" : "bg-white group-hover:bg-cyan-400"}`}
                          ></div>
                        ))}
                      </div>
                      <span className="ml-2 text-sm font-medium text-gray-300 group-hover:text-cyan-400 whitespace-nowrap">
                        More Functions
                      </span>
                    </div>
                  </button>
                  {!showSidebar && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
                    </span>
                  )}
                </div>

                {/* Profile dropdown */}
                <div className="relative ml-3">
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center space-x-2 bg-gray-800 bg-opacity-40 hover:bg-opacity-60 px-3 py-2 rounded-full transition-all duration-300 group"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center overflow-hidden border-2 border-transparent group-hover:border-cyan-400 transition-all duration-300">
                      {user && user.avatarPath ? (
                        <img
                          src={user.avatarPath || "/placeholder.svg"}
                          alt="User Avatar"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="fas fa-user text-gray-300 group-hover:text-cyan-400 transition-all duration-300"></i>
                      )}
                    </div>
                    <span className="text-sm font-medium hidden sm:block whitespace-nowrap">
                      {user && user.fullName}
                    </span>
                    <i
                      className={`fas fa-chevron-down text-xs opacity-70 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : "rotate-0"}`}
                    ></i>
                  </button>

                  {isDropdownOpen && (
                    <div
                      id="dropdown-div"
                      className="absolute right-0 mt-2 w-48 bg-gray-800 bg-opacity-90 backdrop-blur-md rounded-lg shadow-lg py-2 z-50 text-left border border-gray-700 animate-in fade-in slide-in-from-top-5 duration-300"
                    >
                      {!isGuest && (
                        <div>
                          <a
                            href="/profile?tab=personal"
                            className="block px-4 py-2 text-gray-200 hover:bg-gray-700 hover:text-cyan-400 text-sm transition-colors duration-200 flex items-center"
                          >
                            <i className="fas fa-user-circle mr-2"></i>
                            Profile
                          </a>
                          <a
                            href="/profile?tab=password"
                            className="block px-4 py-2 text-gray-200 hover:bg-gray-700 hover:text-cyan-400 text-sm whitespace-nowrap transition-colors duration-200 flex items-center"
                          >
                            <i className="fas fa-key mr-2"></i>
                            Change Password
                          </a>
                          <hr className="my-2 border-gray-700" />
                        </div>
                      )}
                      <a
                        href="#"
                        className="block px-4 py-2 text-gray-200 hover:bg-gray-700 hover:text-cyan-400 text-sm transition-colors duration-200 flex items-center"
                        onClick={openLogoutModal}
                      >
                        <i className="fas fa-sign-out-alt mr-2"></i>
                        Logout
                      </a>
                    </div>
                  )}
                </div>

                {/* Mobile menu button */}
                <div className="md:hidden flex items-center">
                  <button
                    onClick={() => {
                      if (isMobileMenuOpen) {
                        setIsMobileMenuOpen(false)
                      } else {
                        setIsMobileMenuOpen(true)
                      }
                    }}
                    className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-gray-800 hover:bg-opacity-50 focus:outline-none transition-all duration-300"
                    aria-expanded={isMobileMenuOpen}
                  >
                    <span className="sr-only">{isMobileMenuOpen ? "Close main menu" : "Open main menu"}</span>
                    {isMobileMenuOpen ? (
                      <i className="fas fa-times text-xl text-cyan-400"></i>
                    ) : (
                      <i className="fas fa-bars text-xl hover:text-cyan-400 transition-colors duration-300"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <div ref={mobileMenuRef} className={`${isMobileMenuOpen ? "block" : "hidden"} md:hidden`}>
            <div
              className={`px-2 pt-2 pb-3 space-y-1 sm:px-3 ${isHomePage ? "bg-red-900" : "bg-gray-900"} bg-opacity-60 backdrop-blur-md border-t border-gray-800`}
            >
              {navLinks.map((link) => {
                const isActive = currentPath === link.path
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium transition-all duration-300 whitespace-nowrap ${
                      isActive
                        ? "bg-gray-800 bg-opacity-70 text-cyan-400"
                        : "text-gray-300 hover:bg-gray-800 hover:bg-opacity-50 hover:text-white"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className={`${link.icon} ${isActive ? "text-cyan-400" : ""}`}></i>
                    <span>{link.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        </nav>

        {/* Sidebar overlay */}
        {showSidebar && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
            onClick={() => setShowSidebar(false)}
          />
        )}

        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className={`fixed top-0 left-0 h-full w-80 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 shadow-2xl z-50 transform transition-transform duration-300 ease-out border-r border-gray-700 ${
            showSidebar ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Sidebar header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gradient-to-r from-cyan-900/20 to-purple-900/20">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                <i className="fas fa-layer-group text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">More Functions</h2>
                <p className="text-xs text-gray-400">Tools & Features</p>
              </div>
            </div>
            <button
              onClick={() => setShowSidebar(false)}
              className="p-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 text-gray-400 hover:text-white"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-4">
            {/* Recently Used */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">Recently Used</h3>
              <div className="space-y-1">
                {recentlyUsedItems.map((item, index) => {
                  const isActive = item.link && currentPath === item.link
                  const isLiked = likedIds.includes(item.id)
                  return (
                    <div
                      key={index}
                      className="relative group"
                      onMouseEnter={() => setHoveredItem(`recent-${index}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {item.link ? (
                        <Link
                          to={item.link}
                          className={`flex items-center space-x-3 py-2 px-3 rounded-lg transition-all duration-200 ${
                            isActive
                              ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 text-cyan-400"
                              : "hover:bg-gray-700/50 text-gray-300 hover:text-white"
                          }`}
                          onClick={() => {
                            handleItemClick(item.id)
                            setShowSidebar(false)
                          }}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isActive
                                ? "bg-gradient-to-br from-cyan-500 to-purple-600"
                                : "bg-gray-700 group-hover:bg-gray-600"
                            }`}
                          >
                            <i className={`${item.icon} ${isActive ? "text-white" : item.color} text-lg`}></i>
                          </div>
                          <div className="flex-1 text-left">
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <button
                            className={`transition-colors duration-200 ${isLiked ? "text-yellow-400" : "text-gray-500 hover:text-yellow-400"}`}
                            onClick={(e) => toggleLiked(item.id, e)}
                          >
                            <i className={`${isLiked ? "fas" : "far"} fa-star text-sm`}></i>
                          </button>
                        </Link>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            handleItemClick(item.id)
                            item.onClick()
                            setShowSidebar(false)
                          }}
                          className="w-full flex items-center space-x-3 py-2 px-3 rounded-lg transition-all duration-200 hover:bg-gray-700/50 text-gray-300 hover:text-white"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-700 group-hover:bg-gray-600 flex items-center justify-center">
                            <i className={`${item.icon} ${item.color} text-lg`}></i>
                          </div>
                          <div className="flex-1 text-left">
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <button
                            className={`transition-colors duration-200 ${isLiked ? "text-yellow-400" : "text-gray-500 hover:text-yellow-400"}`}
                            onClick={(e) => toggleLiked(item.id, e)}
                          >
                            <i className={`${isLiked ? "fas" : "far"} fa-star text-sm`}></i>
                          </button>
                        </button>
                      )}

                      {/* Tooltip for description on hover */}
                      {hoveredItem === `recent-${index}` && (
                        <div className="fixed left-80 top-1/2 -translate-y-1/2 w-64 bg-gray-900 bg-opacity-95 text-white text-sm rounded-lg p-3 shadow-xl z-[60] backdrop-blur-sm border border-gray-700">
                          <h4 className="text-base font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">
                            {item.name}
                          </h4>
                          <p className="text-gray-300 text-sm">{item.description}</p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Liked Features */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">Liked Features</h3>
              <div className="space-y-1">
                {likedItems.map((item, index) => {
                  const isActive = item.link && currentPath === item.link
                  // Skip if already shown in recently used
                  if (recentlyUsedItems.slice(0, 3).some((recent) => recent.id === item.id)) {
                    return null
                  }

                  return (
                    <div
                      key={index}
                      className="relative group"
                      onMouseEnter={() => setHoveredItem(`liked-${index}`)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {item.link ? (
                        <Link
                          to={item.link}
                          className={`flex items-center space-x-3 py-2 px-3 rounded-lg transition-all duration-200 ${
                            isActive
                              ? "bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 text-cyan-400"
                              : "hover:bg-gray-700/50 text-gray-300 hover:text-white"
                          }`}
                          onClick={() => {
                            handleItemClick(item.id)
                            setShowSidebar(false)
                          }}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              isActive
                                ? "bg-gradient-to-br from-cyan-500 to-purple-600"
                                : "bg-gray-700 group-hover:bg-gray-600"
                            }`}
                          >
                            <i className={`${item.icon} ${isActive ? "text-white" : item.color} text-lg`}></i>
                          </div>
                          <div className="flex-1 text-left">
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <button
                            className="text-yellow-400 transition-colors duration-200"
                            onClick={(e) => toggleLiked(item.id, e)}
                          >
                            <i className="fas fa-star text-sm"></i>
                          </button>
                        </Link>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            handleItemClick(item.id)
                            item.onClick()
                            setShowSidebar(false)
                          }}
                          className="w-full flex items-center space-x-3 py-2 px-3 rounded-lg transition-all duration-200 hover:bg-gray-700/50 text-gray-300 hover:text-white"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-700 group-hover:bg-gray-600 flex items-center justify-center">
                            <i className={`${item.icon} ${item.color} text-lg`}></i>
                          </div>
                          <div className="flex-1 text-left">
                            <span className="font-medium">{item.name}</span>
                          </div>
                          <button
                            className="text-yellow-400 transition-colors duration-200"
                            onClick={(e) => toggleLiked(item.id, e)}
                          >
                            <i className="fas fa-star text-sm"></i>
                          </button>
                        </button>
                      )}

                      {/* Tooltip for description on hover */}
                      {hoveredItem === `liked-${index}` && (
                        <div className="fixed left-80 top-1/2 -translate-y-1/2 w-64 bg-gray-900 bg-opacity-95 text-white text-sm rounded-lg p-3 shadow-xl z-[60] backdrop-blur-sm border border-gray-700">
                          <h4 className="text-base font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">
                            {item.name}
                          </h4>
                          <p className="text-gray-300 text-sm">{item.description}</p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* All Other Tools */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">
                All Other Tools
              </h3>
              <div className="space-y-1">
                {otherItems.map((item, index) => (
                  <div
                    key={index}
                    className="relative group"
                    onMouseEnter={() => setHoveredItem(`other-${index}`)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {item.link ? (
                      <Link
                        to={item.link}
                        className="flex items-center space-x-3 py-2 px-3 rounded-lg transition-all duration-200 hover:bg-gray-700/50 text-gray-300 hover:text-white"
                        onClick={() => {
                          handleItemClick(item.id)
                          setShowSidebar(false)
                        }}
                      >
                        <div className="w-8 h-8 rounded-lg bg-gray-700 group-hover:bg-gray-600 flex items-center justify-center">
                          <i className={`${item.icon} ${item.color} text-lg`}></i>
                        </div>
                        <div className="flex-1 text-left">
                          <span className="font-medium">{item.name}</span>
                        </div>
                        <button
                          className={`transition-colors duration-200 ${likedIds.includes(item.id) ? "text-yellow-400" : "text-gray-500 hover:text-yellow-400"}`}
                          onClick={(e) => toggleLiked(item.id, e)}
                        >
                          <i className={`${likedIds.includes(item.id) ? "fas" : "far"} fa-star text-sm`}></i>
                        </button>
                      </Link>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          handleItemClick(item.id)
                          item.onClick()
                          setShowSidebar(false)
                        }}
                        className="w-full flex items-center space-x-3 py-2 px-3 rounded-lg transition-all duration-200 hover:bg-gray-700/50 text-gray-300 hover:text-white"
                      >
                        <div className="w-8 h-8 rounded-lg bg-gray-700 group-hover:bg-gray-600 flex items-center justify-center">
                          <i className={`${item.icon} ${item.color} text-lg`}></i>
                        </div>
                        <div className="flex-1 text-left">
                          <span className="font-medium">{item.name}</span>
                        </div>
                        <button
                          className={`transition-colors duration-200 ${likedIds.includes(item.id) ? "text-yellow-400" : "text-gray-500 hover:text-yellow-400"}`}
                          onClick={(e) => toggleLiked(item.id, e)}
                        >
                          <i className={`${likedIds.includes(item.id) ? "fas" : "far"} fa-star text-sm`}></i>
                        </button>
                      </button>
                    )}

                    {/* Tooltip for description on hover */}
                    {hoveredItem === `other-${index}` && (
                      <div className="fixed left-80 top-1/2 -translate-y-1/2 w-64 bg-gray-900 bg-opacity-95 text-white text-sm rounded-lg p-3 shadow-xl z-[60] backdrop-blur-sm border border-gray-700">
                        <h4 className="text-base font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">
                          {item.name}
                        </h4>
                        <p className="text-gray-300 text-sm">{item.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar footer */}
          <div className="p-3 border-t border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">{sidebarItems.length} tools available</p>
              <div className="flex space-x-2">
                <span className="text-xs text-gray-500">{recentlyUsedItems.length} recent</span>
                <span className="text-xs text-gray-500">•</span>
                <span className="text-xs text-gray-500">{likedItems.length} liked</span>
              </div>
            </div>
          </div>
        </div>

        {showLecturePopup && (
          <LatexGenerator
            latexCodeSent={`
              ------------------------
              Quantum Physics
              1. Wave-Particle Duality
              2. Superposition Principle
              3. Quantum Entanglement
              4. Heisenberg Uncertainty Principle
              5. Quantum Tunneling
              ------------------------`}
            onClose={() => setShowLecturePopup(false)}
          />
        )}

        {showSlidesPopup && (
          <SlidesGenerator
            latexCodeSent={`
              ------------------------
              Quantum Physics
              1. Wave-Particle Duality
              2. Superposition Principle
              3. Quantum Entanglement
              4. Heisenberg Uncertainty Principle
              5. Quantum Tunneling
              ------------------------`}
            onClose={() => setSlidesPopup(false)}
          />
        )}

        {(showLecturePopup || showSlidesPopup) && <div className="blur-background"></div>}

        {isLogoutModalOpen && (
          <div id="logout-modal-div" className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" onClick={closeLogoutModal}></div>
              <div className="relative bg-gray-900 bg-opacity-90 rounded-lg max-w-md w-full p-6 border border-gray-800 shadow-xl shadow-cyan-500/10 animate-in zoom-in-95 duration-300">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-white">Logout</h3>
                  <button
                    onClick={closeLogoutModal}
                    className="!text-gray-400 !hover:text-cyan-400 transition-colors duration-200"
                  >
                    <i className="fas fa-close"></i>
                  </button>
                </div>
                <div className="my-2 -mx-6 border-t border-gray-800"></div>
                <div className="mb-6">
                  <p className="text-gray-300">Are you sure you want to log out?</p>
                </div>
                <div className="flex justify-center space-x-4">
                  <CancelButton onClick={closeLogoutModal}>Cancel</CancelButton>
                  <Button
                    onClick={handleLogout}
                    className="!rounded-button whitespace-nowrap px-6 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white hover:from-cyan-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-300"
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Floating sidebar toggle button - always visible on left side */}
      <button
        onClick={toggleSidebar}
        className={`fixed left-1 top-1/2 -translate-y-1/2 z-30 py-20 px-1 rounded-full shadow-lg transition-all duration-300 group ${
          showSidebar
            ? "bg-cyan-500 text-white scale-110"
            : "bg-gray-800 bg-opacity-90 text-gray-300 hover:bg-amber-500 hover:text-white hover:scale-110"
        } backdrop-blur-sm border border-gray-600 hover:border-cyan-400`}
        aria-label="Toggle sidebar"
        title="Open More Functions"
      >
        <div className={`transition-transform duration-300 ${showSidebar ? "rotate-180" : ""}`}>
          <i className={`fas fa-chevron-right text-lg ${showSidebar ? "rotate-180" : ""}`}></i>
        </div>

        {/* Pulse animation when sidebar is closed */}
        {!showSidebar && <div className="absolute inset-0 rounded-full bg-cyan-500 opacity-10 animate-ping"></div>}

        {/* Tooltip */}
        <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
          More Functions
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
        </div>
      </button>
    </>
  )
}
